import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import {
  currentOrganizationSelector,
  tariffesArraySelector,
  userSelector,
} from "../../store/selectors";
import plusIcon from "../../assets/plus-icon.svg";
import ProfileSidebar from "../../components/ProfileSidebar/ProfileSidebar";
import "./AdminScreen.css";
import momentTz from "moment-timezone";
import moment from "moment";
import Avatar from "../../components/Avatar/Avatar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import { ReactComponent as MenuIcon } from "../../assets/mobile/menu.svg";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import AppLogo from "../../assets/AppLogoBlue.svg";
import ProfilePopup from "../../components/ProfilePopup/ProfilePopup";
import NotificationsPopup from "../../components/NotificationsPopup/NotificationsPopup";
import { deleteTariff, fetchTariffes } from "../../store/tariffesSlice";
import AddTariffModal from "../../components/AddTariffModal/AddTariffModal";
import EditTariffModal from "../../components/EditTariffModal/EditTariffModal";
import TariffVersionsModal from "../../components/TariffVersionsModal/TariffVersionsModal";
import AdminSharePopup from "./AdminSharePopup";
import useIsMobile from "../../hooks/useIsMobile";
import AdminShareModal from "../../components/AdminShareModal/AdminShareModal";
import { createUserSubscription } from "../../store/usersSlice";
import toast from "react-hot-toast";

const defaultAttributesArray = [
  {
    code: "AVAILABLE_PROJECT_COUNT",
    name: "Количество проектов",
    valueType: "integer",
    description: "Максимальное количество проектов в организации",
  },
  {
    code: "AVAILABLE_SPACE_MB",
    name: "Место на диске (МБ)",
    valueType: "integer",
    description: "Максимальный суммарный размер всех видео в организации",
  },
  {
    code: "AVAILABLE_USER_COUNT",
    name: "Участников в проекте",
    valueType: "integer",
    description: "Максимальное число участников в проекте",
  },
  {
    code: "AVAILABLE_VIEWER_COUNT",
    name: "Внешних участников в проекте",
    valueType: "integer",
    description: "Максимальное число внешних участников в проекте",
  },
  {
    code: "ALLOW_RELEASE_NOTES",
    name: "Доступны Release Notes",
    valueType: "boolean",
    description: "Включение функции release notes",
  },
];

const eternalTariffesIds = [8, 19, 21];

const AdminScreen = () => {
  const user = useSelector(userSelector);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const currentOrganization = useSelector(currentOrganizationSelector);
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const userName = user?.email;
  const tariffesArray = useSelector(tariffesArraySelector);
  const [isAddTariffModalOpen, setIsAddTariffModalOpen] = useState(false);
  const [tariffToEdit, setTariffToEdit] = useState(null);
  const [tariffToSeeVersions, setTariffToSeeVersions] = useState(null);
  const [tariffToShare, setTariffToShare] = useState(null);

  useEffect(() => {
    document.title = "7cut • Админка";
  }, []);

  useEffect(() => {
    dispatch(fetchTariffes({ isDeleted: false })).then((res) => {});
  }, []);

  const formatDate = (dateString) => {
    const res = momentTz(dateString)
      .tz("etc/gmt-3")
      .format("DD.MM.YYYY, HH:mm");
    return res;
  };

  const formatBoolValue = (value) => {
    return value ? "Да" : "Нет";
  };

  const onPressAddTariff = () => {
    setIsAddTariffModalOpen(true);
  };

  const handleCreateTariff = () => {
    dispatch(fetchTariffes({ isDeleted: false }));
  };

  const handleClickDeleteTariff = (tariffId) => {
    if (eternalTariffesIds.includes(tariffId)) return;
    if (confirm("Вы действительно хотите удалить тариф?")) {
      dispatch(deleteTariff(tariffId)).then((res) => {
        if (!res.error) {
          dispatch(fetchTariffes({ isDeleted: false }));
        }
      });
    }
  };

  const handleStartTesterTrial = () => {
    if (
      confirm(
        "Текущая подписка организации сбросится, будет добавлен тариф tester со сроком триала 1 день"
      )
    ) {
      const testerTariff = tariffesArray.find((tar) => tar.id === 21);
      const testerPrice =
        testerTariff &&
        testerTariff.prices &&
        testerTariff.prices.find((price) => price.periodUnit === "MONTH");

      const testerPriceId = (testerPrice && testerPrice.id) || 190;

      const trialDays = 1;
      const trialStartDate = moment().format("YYYY-MM-DD");

      const trialEndDateObject = moment().add(trialDays, "days");
      const trialEndDate = trialEndDateObject.format("YYYY-MM-DD");

      dispatch(
        createUserSubscription({
          organizationId: currentOrganization.id,
          subscriptionData: {
            planVersionPriceId: testerPriceId,
          },
          trialStartDate,
          trialEndDate,
          status: "TRIALING",
        })
      ).then((res) => {
        if (!res.error) {
          toast.success(
            `Ваш бесплатный период активирован до ${trialEndDateObject.format("DD.MM.YYYY")}`
          );
          navigate("/home");
        } else {
          toast.error("Произошла ошибка");
        }
      });
    }
  };

  const renderCurrentTariffValues = (tariffInfo) => {
    const monthPriceValue = tariffInfo.prices.find(
      (price) => price.periodUnit === "MONTH"
    );
    const monthPriceString =
      (monthPriceValue && monthPriceValue.price) || "Бесплатно";
    const yearPriceValue = tariffInfo.prices.find(
      (price) => price.periodUnit === "YEAR"
    );
    const yearPriceString =
      (yearPriceValue && yearPriceValue.price) || "Бесплатно";
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <span>В месяц (руб.): {monthPriceString}</span>
        <span>В год (руб.): {yearPriceString}</span>
        {tariffInfo.attributes.map((attr) => (
          <span>
            {attr.name}:{" "}
            {attr.value === 0
              ? "Безлимит"
              : attr.code === "allow_release_notes"
                ? formatBoolValue(attr.value)
                : attr.value}
          </span>
        ))}
      </div>
    );
  };

  const handleEditTariff = () => {};

  return (
    <div className={isMobile ? "App-mobile" : "adminScreen-App"}>
      {/* HEADER */}
      <div
        className={isMobile ? "mainScreenHeader-mobile" : "adminScreen-header"}
      >
        <div className={isMobile ? "header-left-mobile" : "header-left"}>
          {isMobile && (
            <div
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ display: "flex", marginTop: 7 }}
            >
              <MenuIcon color="white" />
            </div>
          )}
          <div
            onClick={() => navigate("/home", { replace: true })}
            className={"mainScreen-logoBlock"}
            style={{ cursor: "pointer" }}
          >
            <img src={AppLogo} style={{ height: "100%" }} alt="logo" />
          </div>
        </div>
        <div className="header-actions">
          <NotificationsPopup />
          <ProfilePopup />
        </div>
      </div>
      <div className="admin-container" style={{ alignItems: "flex-start" }}>
        {((!isMobile || isMenuOpen) && (
          <ProfileSidebar activeTab="admin" subActive="tariffes" />
        )) ||
          null}
        {/* <aside className="admin-sidebar">
          <nav>
            <ul>
              <li onClick={() => navigate("/profile/admin/dashboard")}>
                Дашборд
              </li>
            </ul>
            <ul>
              <li className="active">Тарифы</li>
            </ul>
            <ul onClick={() => navigate("/profile/admin/organizations")}>
              <li className="">Организации</li>
            </ul>
          </nav>
        </aside> */}
        <main className="admin-main">
          <div style={{ display: "flex", flexDirection: "row" }}>
            <h1 className="admin-title">Тарифы</h1>
            <button className="admin-add-tariff-btn" onClick={onPressAddTariff}>
              <img src={plusIcon} alt="plus" width={16} height={16} />
              Добавить тариф
            </button>
            <button
              className="admin-add-tariff-btn"
              onClick={handleStartTesterTrial}
            >
              Начать триал тарифа tester
            </button>
          </div>

          <TableContainer
            component={Paper}
            style={{
              backgroundColor: "transparent",
              color: "white",
              boxShadow: "none",
              overflowX: isMobile ? "scroll" : "hidden",
              overflowY: "scroll",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    id
                  </TableCell>
                  <TableCell style={{ color: "white", fontWeight: "bold" }}>
                    Название
                  </TableCell>
                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Описание
                  </TableCell>

                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Текущие значения
                  </TableCell>
                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Последнее обновление
                  </TableCell>
                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Обновить
                  </TableCell>
                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Версии
                  </TableCell>
                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Поделиться
                  </TableCell>
                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Удалить
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tariffesArray.map((row) => (
                  <TableRow
                    key={row.name}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell style={{ color: "white" }} align="left">
                      {row.id}
                    </TableCell>
                    <TableCell
                      style={{ color: "white" }}
                      component="th"
                      scope="row"
                    >
                      {row.name}
                    </TableCell>
                    <TableCell style={{ color: "white" }} align="left">
                      {row.description}
                    </TableCell>

                    <TableCell style={{ color: "white" }} align="left">
                      {renderCurrentTariffValues(row)}
                    </TableCell>
                    <TableCell style={{ color: "white" }} align="left">
                      {formatDate(row.releasedAt)}
                    </TableCell>
                    <TableCell align="center">
                      <div
                        onClick={() => setTariffToEdit(row)}
                        style={{
                          backgroundColor: "white",
                          padding: 10,
                          borderRadius: 8,
                          width: 100,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxSizing: "border-box",
                          cursor: "pointer",
                          alignSelf: "flex-end",
                        }}
                      >
                        Обновить
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <div
                        onClick={() => setTariffToSeeVersions(row)}
                        style={{
                          backgroundColor: "white",
                          padding: 10,
                          borderRadius: 8,
                          width: 100,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxSizing: "border-box",
                          cursor: "pointer",
                          alignSelf: "flex-end",
                        }}
                      >
                        Версии
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <div
                        onClick={() => setTariffToShare(row)}
                        style={{
                          backgroundColor: "white",
                          padding: 10,
                          borderRadius: 8,
                          width: 100,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxSizing: "border-box",
                          cursor: "pointer",
                          alignSelf: "flex-end",
                        }}
                      >
                        Поделиться
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <div
                        onClick={() => handleClickDeleteTariff(row.id)}
                        style={{
                          backgroundColor: "white",
                          padding: 10,
                          borderRadius: 8,
                          width: 100,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxSizing: "border-box",
                          cursor: eternalTariffesIds.includes(row.id)
                            ? "default"
                            : "pointer",
                          opacity: eternalTariffesIds.includes(row.id)
                            ? 0.5
                            : 1,
                          alignSelf: "flex-end",
                        }}
                      >
                        Удалить
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/* <h1 className="admin-title">Доступные атрибуты тарифов</h1> */}
          {/* <TableContainer
            component={Paper}
            style={{
              backgroundColor: "transparent",
              color: "white",
              overflow: "hidden",
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell style={{ color: "white", fontWeight: "bold" }}>
                    Название
                  </TableCell>
                  <TableCell style={{ color: "white", fontWeight: "bold" }}>
                    id
                  </TableCell>

                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Описание
                  </TableCell>
                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="right"
                  >
                    Тип значения
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {defaultAttributesArray.map((row) => (
                  <TableRow
                    key={row.name}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell style={{ color: "white" }} align="left">
                      {row.name}
                    </TableCell>
                    <TableCell
                      style={{ color: "white" }}
                      component="th"
                      scope="row"
                    >
                      {row.code}
                    </TableCell>

                    <TableCell style={{ color: "white" }} align="left">
                      {row.description}
                    </TableCell>
                    <TableCell style={{ color: "white" }} align="right">
                      Число (integer)
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer> */}
        </main>

        {isAddTariffModalOpen && (
          <AddTariffModal
            onClose={() => setIsAddTariffModalOpen(false)}
            onSubmit={handleCreateTariff}
          />
        )}
        {tariffToEdit && (
          <EditTariffModal
            tariffInfo={tariffToEdit}
            onClose={() => setTariffToEdit(null)}
            onSubmit={handleCreateTariff}
          />
        )}
        {tariffToSeeVersions && (
          <TariffVersionsModal
            tariffInfo={tariffToSeeVersions}
            onClose={() => setTariffToSeeVersions(null)}
          />
        )}
      </div>
      {tariffToShare && (
        <AdminShareModal
          isOpen={!!tariffToShare}
          tariff={tariffToShare}
          onClose={() => setTariffToShare(null)}
        />
      )}
    </div>
  );
};

export default AdminScreen;
